"use client";

import * as React from "react";
import { motion, MotionConfig, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { MessageSquare, X, Send, RotateCcw, Loader2, HelpCircle, Accessibility, Contrast, Type, Link2, Volume2 } from "lucide-react";
import { useJewelryAdvisor } from "@/hooks/useJewelryAdvisor";
import { ChatMessage } from "./ChatMessage";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const transition = {
  type: "spring" as const,
  bounce: 0,
  duration: 0.3,
};

interface AccessibilitySettings {
  highContrast: boolean;
  largeText: boolean;
  highlightLinks: boolean;
  textToSpeech: boolean;
}

export function JewelryAdvisorBot() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [showAccessibility, setShowAccessibility] = React.useState(false);
  const [accessibility, setAccessibility] = React.useState<AccessibilitySettings>({
    highContrast: false,
    largeText: false,
    highlightLinks: false,
    textToSpeech: false,
  });
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { messages, isLoading, sendMessage, resetConversation } = useJewelryAdvisor();

  // Text-to-Speech function
  const speakText = React.useCallback((text: string) => {
    if (!accessibility.textToSpeech || !text) return;
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'he-IL';
    utterance.rate = 0.9;
    utterance.pitch = 1;
    
    window.speechSynthesis.speak(utterance);
  }, [accessibility.textToSpeech]);

  // Speak new assistant messages
  React.useEffect(() => {
    if (accessibility.textToSpeech && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && lastMessage.content) {
        speakText(lastMessage.content);
      }
    }
  }, [messages, accessibility.textToSpeech, speakText]);

  // Stop speech when TTS is disabled
  React.useEffect(() => {
    if (!accessibility.textToSpeech) {
      window.speechSynthesis.cancel();
    }
  }, [accessibility.textToSpeech]);

  // Scroll to bottom when messages change
  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Focus input when opened
  React.useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleSend = () => {
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue.trim(), false);
      setInputValue("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOptionSelect = (value: string) => {
    if (!isLoading) {
      sendMessage(value, true);
    }
  };

  const toggleAccessibility = (key: keyof AccessibilitySettings) => {
    setAccessibility(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Generate accessibility classes
  const accessibilityClasses = cn(
    accessibility.highContrast && "bot-high-contrast",
    accessibility.largeText && "bot-large-text",
    accessibility.highlightLinks && "bot-highlight-links"
  );

  return (
    <MotionConfig transition={transition}>
      <div className="fixed bottom-4 left-4 z-50" dir="rtl">
        <motion.div
          initial={false}
          animate={{
            width: isOpen ? "min(380px, calc(100vw - 32px))" : 56,
            height: isOpen ? "min(520px, calc(100vh - 100px))" : 56,
            borderRadius: isOpen ? "0px" : "12px",
          }}
          className={cn(
            "overflow-hidden bg-card shadow-xl border border-border",
            accessibilityClasses
          )}
          style={{
            ...(accessibility.highContrast && {
              background: '#000',
              color: '#fff',
              borderColor: '#fff',
            }),
          }}
        >
          {/* Header */}
          <motion.div
            className={cn(
              `flex items-center ${isOpen ? 'justify-between' : 'justify-center'} px-4 py-3 border-b border-border bg-gradient-to-l from-primary/10 to-transparent h-14`,
              accessibility.highContrast && "bg-black border-white"
            )}
            initial={false}
            animate={{
              borderRadius: "0",
            }}
          >
            {isOpen ? (
              <>
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2"
                >
                  <HelpCircle className={cn("h-5 w-5", accessibility.highContrast ? "text-yellow-400" : "text-primary")} />
                  <span className={cn(
                    "font-medium",
                    accessibility.highContrast ? "text-white" : "text-foreground",
                    accessibility.largeText && "text-base"
                  )}>
                    עזרה
                  </span>
                </motion.div>

                <div className="flex items-center gap-1 relative">
                  {/* Accessibility button */}
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <motion.button
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          onClick={() => setShowAccessibility(!showAccessibility)}
                          className={cn(
                            "p-2 rounded-full transition-colors",
                            showAccessibility 
                              ? "bg-primary text-primary-foreground" 
                              : accessibility.highContrast 
                                ? "hover:bg-white/20" 
                                : "hover:bg-muted"
                          )}
                          title="נגישות"
                        >
                          <Accessibility className={cn(
                            "h-4 w-4",
                            showAccessibility 
                              ? "" 
                              : accessibility.highContrast ? "text-white" : "text-muted-foreground"
                          )} />
                        </motion.button>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs">
                        נגישות
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  {/* Accessibility dropdown */}
                  <AnimatePresence>
                    {showAccessibility && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: -10 }}
                        className={cn(
                          "absolute top-10 left-0 border rounded-lg shadow-xl p-2 w-40 space-y-1 z-50",
                          accessibility.highContrast 
                            ? "bg-black border-white" 
                            : "bg-card border-border"
                        )}
                      >
                        {/* High Contrast */}
                        <button
                          onClick={() => toggleAccessibility('highContrast')}
                          className={cn(
                            "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors text-right",
                            accessibility.highContrast 
                              ? "bg-yellow-400 text-black" 
                              : "hover:bg-muted"
                          )}
                        >
                          <Contrast className="h-4 w-4 shrink-0" />
                          <span>ניגודיות</span>
                        </button>

                        {/* Large Text */}
                        <button
                          onClick={() => toggleAccessibility('largeText')}
                          className={cn(
                            "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors text-right",
                            accessibility.largeText 
                              ? "bg-primary text-primary-foreground" 
                              : accessibility.highContrast 
                                ? "hover:bg-white/20 text-white" 
                                : "hover:bg-muted"
                          )}
                        >
                          <Type className="h-4 w-4 shrink-0" />
                          <span>הגדלת טקסט</span>
                        </button>

                        {/* Highlight Links */}
                        <button
                          onClick={() => toggleAccessibility('highlightLinks')}
                          className={cn(
                            "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors text-right",
                            accessibility.highlightLinks 
                              ? "bg-primary text-primary-foreground" 
                              : accessibility.highContrast 
                                ? "hover:bg-white/20 text-white" 
                                : "hover:bg-muted"
                          )}
                        >
                          <Link2 className="h-4 w-4 shrink-0" />
                          <span>הדגשת קישורים</span>
                        </button>

                        {/* Text to Speech */}
                        <button
                          onClick={() => toggleAccessibility('textToSpeech')}
                          className={cn(
                            "w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors text-right",
                            accessibility.textToSpeech 
                              ? "bg-primary text-primary-foreground" 
                              : accessibility.highContrast 
                                ? "hover:bg-white/20 text-white" 
                                : "hover:bg-muted"
                          )}
                        >
                          <Volume2 className="h-4 w-4 shrink-0" />
                          <span>קריאה קולית</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Reset button */}
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={resetConversation}
                    className={cn(
                      "p-2 rounded-full hover:bg-muted transition-colors",
                      accessibility.highContrast && "hover:bg-white/20"
                    )}
                    title="התחל מחדש"
                  >
                    <RotateCcw className={cn(
                      "h-4 w-4",
                      accessibility.highContrast ? "text-white" : "text-muted-foreground"
                    )} />
                  </motion.button>

                  {/* Close button */}
                  <motion.button
                    className={cn(
                      "p-2 rounded-full hover:bg-muted transition-colors",
                      accessibility.highContrast && "hover:bg-white/20"
                    )}
                    onClick={() => {
                      setIsOpen(false);
                      setShowAccessibility(false);
                      window.speechSynthesis.cancel();
                    }}
                  >
                    <X className={cn(
                      "h-4 w-4",
                      accessibility.highContrast ? "text-white" : "text-foreground"
                    )} />
                  </motion.button>
                </div>
              </>
            ) : (
              <motion.button
                className="flex items-center justify-center w-full h-full"
                onClick={() => setIsOpen(true)}
              >
                <MessageSquare className="h-6 w-6 text-primary" />
              </motion.button>
            )}
          </motion.div>

          {/* Messages */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                ref={scrollRef}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={cn(
                  "flex-1 overflow-y-auto px-4 py-3 space-y-3",
                  accessibility.highContrast && "bg-black",
                  accessibility.largeText && "[&_p]:text-base [&_span]:text-base [&_button]:text-base",
                  accessibility.highlightLinks && "[&_a]:bg-yellow-300 [&_a]:text-black [&_a]:px-1 [&_a]:rounded [&_a]:font-bold"
                )}
                style={{ height: "calc(100% - 120px)" }}
              >
                {messages.length === 0 ? (
                  <p className={cn(
                    "text-center text-muted-foreground text-sm py-8",
                    accessibility.highContrast && "text-white",
                    accessibility.largeText && "text-base"
                  )}>
                    שלום! אני כאן לעזור
                  </p>
                ) : (
                  messages.map((message, index) => (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      onOptionSelect={handleOptionSelect}
                      isLatest={index === messages.length - 1}
                      accessibility={accessibility}
                    />
                  ))
                )}

                {/* Loading indicator */}
                {isLoading && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className={cn(
                      "bg-muted rounded-2xl rounded-tr-sm px-4 py-3",
                      accessibility.highContrast && "bg-white/10"
                    )}>
                      <div className={cn(
                        "flex items-center gap-2",
                        accessibility.highContrast ? "text-white" : "text-muted-foreground"
                      )}>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className={cn("text-sm", accessibility.largeText && "text-base")}>חושב...</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Input */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className={cn(
                  "absolute bottom-0 left-0 right-0 p-3 border-t border-border bg-card",
                  accessibility.highContrast && "bg-black border-white"
                )}
              >
                <div className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="הקלד הודעה..."
                    className={cn(
                      "flex-1",
                      accessibility.highContrast && "bg-white text-black border-white",
                      accessibility.largeText && "text-base"
                    )}
                    disabled={isLoading}
                  />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isLoading}
                    className={cn(
                      "p-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors",
                      accessibility.highContrast && "bg-yellow-400 text-black hover:bg-yellow-300"
                    )}
                  >
                    <Send className="h-4 w-4" />
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </MotionConfig>
  );
}
